import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Pagination,
  TextField,
  Button,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { styled } from '@mui/system';
import debounce from 'lodash/debounce';

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
  background: '#fff',
  overflow: 'hidden',
  fontFamily: '"Inter", sans-serif',
  [theme.breakpoints.down('sm')]: {
    margin: '8px',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: '1px solid #f0f0f0',
  color: '#333',
  fontSize: '0.95rem',
  fontWeight: 500,
  fontFamily: '"Inter", sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.85rem',
    padding: '8px',
  },
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  fontWeight: 700,
  color: '#666',
  background: '#f8f9fa',
  textTransform: 'uppercase',
  fontSize: '0.9rem',
  fontFamily: '"Inter", sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  border: '2px solid #21ccbb',
  borderRadius: '8px',
  marginRight: '12px',
  [theme.breakpoints.down('sm')]: {
    width: 32,
    height: 32,
    marginRight: '8px',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '4px 12px',
  fontSize: '0.85rem',
  transition: 'all 0.3s ease',
  background: 'linear-gradient(135deg, #21ccbb, #1aa89b)',
  color: '#fff',
  fontFamily: '"Inter", sans-serif',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(33, 204, 187, 0.4)',
    background: 'linear-gradient(135deg, #1aa89b, #21ccbb)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '3px 8px',
    fontSize: '0.75rem',
  },
}));

const DeleteButton = styled(StyledButton)({
  background: 'linear-gradient(135deg, #ff4d4f, #d32f2f)',
  '&:hover': {
    background: 'linear-gradient(135deg, #d32f2f, #ff4d4f)',
    boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
  },
});

const ResetButton = styled(StyledButton)({
  background: 'linear-gradient(135deg, #9e9e9e, #757575)',
  '&:hover': {
    background: 'linear-gradient(135deg, #757575, #9e9e9e)',
    boxShadow: '0 4px 12px rgba(117, 117, 117, 0.4)',
  },
});

const StyledPagination = styled(Pagination)(({ theme }) => ({
  marginTop: theme.spacing(3),
  '& .MuiPaginationItem-root': {
    color: '#21ccbb',
    fontFamily: '"Inter", sans-serif',
    '&:hover': {
      backgroundColor: 'rgba(33, 204, 187, 0.1)',
    },
  },
  '& .Mui-selected': {
    backgroundColor: '#21ccbb !important',
    color: '#fff',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  background: '#fff',
  borderRadius: '8px',
  fontFamily: '"Inter", sans-serif',
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: '#21ccbb' },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  minWidth: 120,
  background: '#fff',
  borderRadius: '8px',
  fontFamily: '"Inter", sans-serif',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#21ccbb' },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: '#21ccbb',
  thickness: 5,
  size: 50,
  [theme.breakpoints.down('sm')]: {
    size: 40,
  },
}));

const SearchCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: '#21ccbb',
  thickness: 4,
  size: 20,
  marginRight: theme.spacing(1),
}));

const Restaurants = () => {
  const { user } = useContext(AuthContext);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [filters, setFilters] = useState({
    typeAbonnement: 'ALL',
    typeBoutique: 'ALL',
    nomSociete: 'ALL',
  });
  const [deleteLoading, setDeleteLoading] = useState({});
  const [editLoading, setEditLoading] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const navigate = useNavigate();
  const theme = useTheme();

  // Debounce for updating search state
  const debouncedSetSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPage(0);
    }, 200),
    []
  );

  // Debounce for API calls
  const debouncedFetchRestaurants = useCallback(
    debounce((query, filters, page, rowsPerPage) => {
      fetchRestaurants(query, filters, page, rowsPerPage);
    }, 200),
    []
  );

  useEffect(() => {
    if (search.trim()) {
      setSearchLoading(true);
      debouncedFetchRestaurants(search.trim(), filters, page, rowsPerPage);
    } else {
      fetchRestaurants(search.trim(), filters, page, rowsPerPage);
    }
    return () => {
      debouncedFetchRestaurants.cancel();
    };
  }, [search, filters, page, rowsPerPage, debouncedFetchRestaurants]);

  const fetchRestaurants = async (searchQuery, filters, page, rowsPerPage) => {
    try {
      setLoading(!searchQuery);
      let url = `https://apibackoffice.lalivraison.ma/api/restaurants?page=${page + 1}&limit=${rowsPerPage}`;

      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }

      if (filters.typeAbonnement !== 'ALL') {
        if (filters.typeAbonnement === 'AVEC_LIVREUR') {
          url += `&type_abonnement=2`;
        } else if (filters.typeAbonnement === 'SANS_LIVREUR') {
          url += `&type_abonnement=0,1`;
        }
      }

      if (filters.typeBoutique !== 'ALL') {
        url += `&type_boutique=${encodeURIComponent(filters.typeBoutique)}`;
      }

      if (filters.nomSociete !== 'ALL') {
        url += `&nom_societe=${encodeURIComponent(filters.nomSociete)}`;
      }

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch restaurants');
      }
      if (!Array.isArray(data.restaurants)) {
        console.error('API response does not contain restaurants array:', data);
        setRestaurants([]);
        setTotal(0);
        setError('Invalid response from server');
      } else {
        setRestaurants(data.restaurants);
        setTotal(data.total || 0);
        setError('');
      }
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setError(error.message);
      setRestaurants([]);
      setTotal(0);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleDelete = async (id, event) => {
    event.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this restaurant?')) return;
    try {
      setDeleteLoading((prev) => ({ ...prev, [id]: true }));
      const response = await fetch(`https://apibackoffice.lalivraison.ma/api/restaurants/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error deleting restaurant');
      }
      setSnackbar({ open: true, message: 'Restaurant deleted successfully' });
      await fetchRestaurants(search.trim(), filters, page, rowsPerPage); // Refetch restaurants
    } catch (err) {
      setError(err.message || 'Error deleting restaurant');
    } finally {
      setDeleteLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleEdit = async (id, event) => {
    if (event) event.stopPropagation();
    try {
      setEditLoading((prev) => ({ ...prev, [id]: true }));
      navigate(`/edit-restaurant/${id}`);
    } finally {
      setEditLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    debouncedSetSearch(value);
  };

  const handleReset = () => {
    setInputValue('');
    setSearch('');
    setFilters({
      typeAbonnement: 'ALL',
      typeBoutique: 'ALL',
      nomSociete: 'ALL',
    });
    setPage(0);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ open: false, message: '' });
  };

  const getTypeAbonnement = (typeAbonnement) => {
    return typeAbonnement === 2 ? 'Avec livreur' : 'Sans livreur';
  };

  // Memoize table rows to reduce re-renders
  const restaurantRows = useMemo(() => {
    return restaurants.map((restaurant) => (
      <TableRow
        key={restaurant.id}
        sx={{
          '&:hover': {
            background: '#f8f9fa',
            cursor: 'pointer',
          },
        }}
        onClick={() => handleEdit(restaurant.id)}
      >
        <StyledTableCell>
          <StyledAvatar
            src={`https://lalivraison.ma/assets/img/images/lalivraison/${restaurant.url_unique}/articles/${restaurant.image}`}
            alt={restaurant.designation}
          />
        </StyledTableCell>
        <StyledTableCell>
          <Box>
            <StyledTypography sx={{ fontWeight: 600 }}>{restaurant.designation || 'N/A'}</StyledTypography>
            <StyledTypography sx={{ fontSize: '0.85rem', color: '#666' }}>
              {restaurant.email || 'N/A'}
            </StyledTypography>
          </Box>
        </StyledTableCell>
        <StyledTableCell>{restaurant.url_unique || 'N/A'}</StyledTableCell>
        <StyledTableCell>{restaurant.type_boutique || 'N/A'}</StyledTableCell>
        <StyledTableCell>{getTypeAbonnement(restaurant.type_abonnement)}</StyledTableCell>
        <StyledTableCell>{restaurant.nom_societe || 'N/A'}</StyledTableCell>
        <StyledTableCell>
          {user && ['admin', 'manager'].includes(user.role) && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <StyledButton
                variant="contained"
                onClick={(event) => handleEdit(restaurant.id, event)}
                disabled={editLoading[restaurant.id] || deleteLoading[restaurant.id]}
                startIcon={editLoading[restaurant.id] && <CircularProgress size={16} color="inherit" />}
              >
                Edit
              </StyledButton>
              {user.role === 'admin' && (
                <DeleteButton
                  variant="contained"
                  onClick={(event) => handleDelete(restaurant.id, event)}
                  disabled={deleteLoading[restaurant.id] || editLoading[restaurant.id]}
                  startIcon={deleteLoading[restaurant.id] && <CircularProgress size={16} color="inherit" />}
                >
                  Delete
                </DeleteButton>
              )}
            </Box>
          )}
        </StyledTableCell>
      </TableRow>
    ));
  }, [restaurants, user, deleteLoading, editLoading]);

  const uniqueNomSocietes = [...new Set(restaurants.map((r) => r.nom_societe).filter(Boolean))];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: '1400px', mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', mb: 3, gap: 2, alignItems: 'center' }}>
        <StyledTextField
          placeholder="Search by designation, URL, or nom societe..."
          value={inputValue}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{ flex: '1 1 200px', minWidth: '200px' }}
          InputProps={{
            endAdornment: searchLoading && inputValue.trim() ? <SearchCircularProgress /> : null,
          }}
        />
        <StyledSelect
          value={filters.typeAbonnement}
          onChange={(e) => handleFilterChange('typeAbonnement', e.target.value)}
          variant="outlined"
          size="small"
        >
          <MenuItem value="ALL">All Type Abonnement</MenuItem>
          <MenuItem value="AVEC_LIVREUR">Avec livreur</MenuItem>
          <MenuItem value="SANS_LIVREUR">Sans livreur</MenuItem>
        </StyledSelect>
        <StyledSelect
          value={filters.typeBoutique}
          onChange={(e) => handleFilterChange('typeBoutique', e.target.value)}
          variant="outlined"
          size="small"
        >
          <MenuItem value="ALL">All Type Boutique</MenuItem>
          <MenuItem value="Restaurant">Restaurant</MenuItem>
          <MenuItem value="Patisserie">Patisserie</MenuItem>
        </StyledSelect>
        <StyledSelect
          value={filters.nomSociete}
          onChange={(e) => handleFilterChange('nomSociete', e.target.value)}
          variant="outlined"
          size="small"
        >
          <MenuItem value="ALL">All Nom Societe</MenuItem>
          {uniqueNomSocietes.map((nom) => (
            <MenuItem key={nom} value={nom}>
              {nom}
            </MenuItem>
          ))}
        </StyledSelect>
        <ResetButton variant="contained" onClick={handleReset}>
          Reset
        </ResetButton>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <StyledCircularProgress />
        </Box>
      ) : error ? (
        <StyledTypography color="error" sx={{ textAlign: 'center', mt: 4, fontSize: '1.2rem' }}>
          {error}
        </StyledTypography>
      ) : restaurants.length === 0 ? (
        <StyledTypography sx={{ textAlign: 'center', color: '#666', fontSize: '1.2rem', py: 4 }}>
          No restaurants found. Try adjusting your search or filters.
        </StyledTypography>
      ) : (
        <>
          <StyledTableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableHeadCell>LOGO</StyledTableHeadCell>
                  <StyledTableHeadCell>DESIGNATION</StyledTableHeadCell>
                  <StyledTableHeadCell>URL UNIQUE</StyledTableHeadCell>
                  <StyledTableHeadCell>TYPE BOUTIQUE</StyledTableHeadCell>
                  <StyledTableHeadCell>TYPE ABONNEMENT</StyledTableHeadCell>
                  <StyledTableHeadCell>NOM SOCIETE</StyledTableHeadCell>
                  <StyledTableHeadCell>ACTIONS</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>{restaurantRows}</TableBody>
            </Table>
          </StyledTableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StyledTypography sx={{ color: '#666' }}>
                Page {page + 1} of {Math.ceil(total / rowsPerPage) || 1}
              </StyledTypography>
              <StyledSelect
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                size="small"
              >
                <MenuItem value={10}>SHOW 10</MenuItem>
                <MenuItem value={25}>SHOW 25</MenuItem>
                <MenuItem value={50}>SHOW 50</MenuItem>
              </StyledSelect>
            </Box>
            <StyledPagination
              count={Math.ceil(total / rowsPerPage) || 1}
              page={page + 1}
              onChange={handleChangePage}
            />
          </Box>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default React.memo(Restaurants);